/**
 * useOrderRealtime Hook
 * Manages real-time order updates via Socket.IO
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { Order } from '../types/order.types';
import { bindSocketTokenRefresh } from '../utils/socketAuth';
import { pushWidgetSnapshot } from './useWidgetSnapshotSync';

// Socket.IO is mounted at root, not under /api/v1
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 
  (process.env.REACT_APP_API_URL ? process.env.REACT_APP_API_URL.replace('/api/v1', '') : 'http://localhost:5000');

interface RealtimeEvent {
  order: Order;
  changes?: Record<string, any>;
  updatedBy?: {
    id: number;
    name: string;
    email: string;
  };
  createdBy?: {
    id: number;
    name: string;
    email: string;
  };
  deletedBy?: {
    id: number;
    name: string;
    email: string;
  };
}

interface DeliveryUpdatedEvent {
  order_id: number;
  order_number: string;
  tracking_number: string;
  old_status: string | null;
  new_status: string;
  status_name: string;
  status_color: string;
  situation: string | null;
  situation_name: string | null;
  situation_color: string | null;
  note: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  updated_at: string;
  silent?: boolean;
  source?: string;
}

interface PresenceUser {
  userId: number;
  firstName?: string;
  lastName?: string;
  pseudo?: string;
  isActive?: boolean;
}

interface PresenceUpdateEvent {
  storeId: number;
  users: PresenceUser[];
}

export interface OrderDiscussionTypingEvent {
  storeId: number;
  orderId: number;
  userId: number;
  displayName: string;
  typing: boolean;
}

interface UseOrderRealtimeProps {
  storeId: number | null;
  accessToken: string | null;
  onOrderCreated?: (event: RealtimeEvent) => void;
  onOrderUpdated?: (event: RealtimeEvent) => void;
  onOrderDeleted?: (event: { orderId: number; orderNumber: string; deletedBy: any }) => void;
  onCommentAdded?: (event: { orderId: number; comment: any; addedBy: any }) => void;
  onCommentUpdated?: (event: { orderId: number; comment: any; updatedBy: any }) => void;
  onCommentDeleted?: (event: { orderId: number; commentId: number; deletedBy: any }) => void;
  onNotificationReceived?: (event: { notification: any; storeId: number }) => void;
  onDeliveryUpdated?: (event: DeliveryUpdatedEvent) => void;
  onSavedSearchCountsUpdated?: (event: { storeId: number; counts: Record<number, number> }) => void;
  onSavedSearchTimeCountsUpdated?: (event: { storeId: number; counts: Record<number, number> }) => void;
  onDashboardStatsUpdated?: (event: { storeId: number }) => void;
  onOrderBulk?: (event: {
    storeId?: number;
    action: string;
    ids: number[];
    patch?: Record<string, unknown> | null;
  }) => void;
  onPresenceUpdated?: (event: PresenceUpdateEvent) => void;
  onDiscussionTyping?: (event: OrderDiscussionTypingEvent) => void;
}

export const useOrderRealtime = ({
  storeId,
  accessToken,
  onOrderCreated,
  onOrderUpdated,
  onOrderDeleted,
  onCommentAdded,
  onCommentUpdated,
  onCommentDeleted,
  onNotificationReceived,
  onDeliveryUpdated,
  onSavedSearchCountsUpdated,
  onSavedSearchTimeCountsUpdated,
  onDashboardStatsUpdated,
  onOrderBulk,
  onPresenceUpdated,
  onDiscussionTyping
}: UseOrderRealtimeProps) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSavedSearchCountsUpdatedRef = useRef(onSavedSearchCountsUpdated);
  useEffect(() => {
    onSavedSearchCountsUpdatedRef.current = onSavedSearchCountsUpdated;
  }, [onSavedSearchCountsUpdated]);

  const onSavedSearchTimeCountsUpdatedRef = useRef(onSavedSearchTimeCountsUpdated);
  useEffect(() => {
    onSavedSearchTimeCountsUpdatedRef.current = onSavedSearchTimeCountsUpdated;
  }, [onSavedSearchTimeCountsUpdated]);

  const onOrderCreatedRef = useRef(onOrderCreated);
  useEffect(() => {
    onOrderCreatedRef.current = onOrderCreated;
  }, [onOrderCreated]);

  const onOrderUpdatedRef = useRef(onOrderUpdated);
  useEffect(() => {
    onOrderUpdatedRef.current = onOrderUpdated;
  }, [onOrderUpdated]);

  const onOrderDeletedRef = useRef(onOrderDeleted);
  useEffect(() => {
    onOrderDeletedRef.current = onOrderDeleted;
  }, [onOrderDeleted]);

  const onCommentAddedRef = useRef(onCommentAdded);
  useEffect(() => {
    onCommentAddedRef.current = onCommentAdded;
  }, [onCommentAdded]);

  const onCommentUpdatedRef = useRef(onCommentUpdated);
  useEffect(() => {
    onCommentUpdatedRef.current = onCommentUpdated;
  }, [onCommentUpdated]);

  const onCommentDeletedRef = useRef(onCommentDeleted);
  useEffect(() => {
    onCommentDeletedRef.current = onCommentDeleted;
  }, [onCommentDeleted]);

  const onNotificationReceivedRef = useRef(onNotificationReceived);
  useEffect(() => {
    onNotificationReceivedRef.current = onNotificationReceived;
  }, [onNotificationReceived]);

  const onOrderBulkRef = useRef(onOrderBulk);
  useEffect(() => {
    onOrderBulkRef.current = onOrderBulk;
  }, [onOrderBulk]);

  const onDashboardStatsUpdatedRef = useRef(onDashboardStatsUpdated);
  useEffect(() => {
    onDashboardStatsUpdatedRef.current = onDashboardStatsUpdated;
  }, [onDashboardStatsUpdated]);

  const onPresenceUpdatedRef = useRef(onPresenceUpdated);
  useEffect(() => {
    onPresenceUpdatedRef.current = onPresenceUpdated;
  }, [onPresenceUpdated]);

  const onDeliveryUpdatedRef = useRef(onDeliveryUpdated);
  useEffect(() => {
    onDeliveryUpdatedRef.current = onDeliveryUpdated;
  }, [onDeliveryUpdated]);

  const onDiscussionTypingRef = useRef(onDiscussionTyping);
  useEffect(() => {
    onDiscussionTypingRef.current = onDiscussionTyping;
  }, [onDiscussionTyping]);

  /**
   * Emit order created event
   */
  const emitOrderCreated = useCallback((order: Order) => {
    if (socket && storeId) {
      socket.emit('order:created', { storeId, order });
    }
  }, [socket, storeId]);

  /**
   * Emit order updated event
   */
  const emitOrderUpdated = useCallback((order: Order, changes?: Record<string, any>) => {
    console.log('🚀 [EMIT DEBUG] order:updated', { 
      orderId: order.id, 
      orderNumber: order.order_number,
      status: order.status, 
      assigned_to: order.assigned_to,
      changes 
    });
    if (socket && storeId) {
      socket.emit('order:updated', { storeId, order, changes });
    }
  }, [socket, storeId]);

  /**
   * Emit order deleted event
   */
  const emitOrderDeleted = useCallback((orderId: number, orderNumber: string) => {
    if (socket && storeId) {
      socket.emit('order:deleted', { storeId, orderId, orderNumber });
    }
  }, [socket, storeId]);

  /**
   * Emit order status changed event
   */
  const emitStatusChanged = useCallback((orderId: number, orderNumber: string, newStatus: string, oldStatus: string) => {
    if (socket && storeId) {
      socket.emit('order:status_changed', { storeId, orderId, orderNumber, newStatus, oldStatus });
    }
  }, [socket, storeId]);

  /**
   * Emit comment added event
   */
  const emitCommentAdded = useCallback((orderId: number, comment: any) => {
    if (socket && storeId) {
      socket.emit('comment:added', { storeId, orderId, comment });
    }
  }, [socket, storeId]);

  /**
   * Emit comment deleted event
   */
  const emitCommentDeleted = useCallback((orderId: number, commentId: number) => {
    if (socket && storeId) {
      socket.emit('comment:deleted', { storeId, orderId, commentId });
    }
  }, [socket, storeId]);

  // Initialize socket connection
  useEffect(() => {
    if (!storeId) {
      return;
    }

    // Get token from localStorage (always fresh, updated by axios interceptor)
    const currentToken = localStorage.getItem('accessToken') || accessToken;
    
    if (!currentToken) {
      return;
    }

    // console.log('🔌 Initializing Socket.IO connection to:', SOCKET_URL);

    // Create socket connection with auth
    const newSocket = io(SOCKET_URL, {
      auth: {
        token: currentToken
      },
      transports: ['websocket', 'polling'], // Prefer websocket, fallback to polling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5
    });
    bindSocketTokenRefresh(newSocket);

    // Connection events
    newSocket.on('connect', () => {
      setIsConnected(true);
      setError(null);
      
      // Join store room
      newSocket.emit('store:join', storeId);
    });

    newSocket.on('store:joined', () => {});

    newSocket.on('disconnect', () => {
      setIsConnected(false);
    });

    newSocket.on('connect_error', (err) => {
      console.error('❌ Socket.IO connection error:', err.message);
      setError(err.message);
      setIsConnected(false);
    });

    newSocket.on('error', (err) => {
      console.error('❌ Socket.IO error:', err);
      setError(err.message || 'Socket error');
    });

    // Order events (use refs so socket does not reconnect when callbacks change)
    newSocket.on('order:created', (data: RealtimeEvent) => {
      onOrderCreatedRef.current?.(data);
      pushWidgetSnapshot();
    });

    newSocket.on('order:updated', (data: RealtimeEvent) => {
      onOrderUpdatedRef.current?.(data);
    });

    newSocket.on('order:deleted', (data: { orderId: number; orderNumber: string; deletedBy: any }) => {
      onOrderDeletedRef.current?.(data);
      pushWidgetSnapshot();
    });

    // Comment events
    newSocket.on('comment:added', (data: { orderId: number; comment: any; addedBy: any }) => {
      onCommentAddedRef.current?.(data);
    });

    newSocket.on('comment:updated', (data: { orderId: number; comment: any; updatedBy: any }) => {
      onCommentUpdatedRef.current?.(data);
    });

    newSocket.on(
      'comment:deleted',
      (data: { orderId: number; commentId: number; deletedBy: any; comment?: any }) => {
        onCommentDeletedRef.current?.(data);
      }
    );

    // Notification events
    newSocket.on('notification:new', (data: { notification: any; storeId: number }) => {
      const eventStoreId = Number(data?.storeId ?? data?.notification?.store_id);
      if (!storeId || !Number.isFinite(eventStoreId) || eventStoreId !== Number(storeId)) {
        return;
      }
      onNotificationReceivedRef.current?.(data);
    });

    // Delivery update events (use ref so socket does not reconnect when callback changes)
    newSocket.on('order:delivery_updated', (data: DeliveryUpdatedEvent) => {
      onDeliveryUpdatedRef.current?.(data);
    });

    // Saved search counts updated events (use ref so socket does not reconnect when callback changes)
    newSocket.on('saved_search:counts_updated', (data: { storeId: number; counts: Record<number, number> }) => {
      if (onSavedSearchCountsUpdatedRef.current) {
        onSavedSearchCountsUpdatedRef.current(data);
      }
      pushWidgetSnapshot();
    });

    newSocket.on('saved_search:time_counts_updated', (data: { storeId: number; counts: Record<number, number> }) => {
      if (onSavedSearchTimeCountsUpdatedRef.current) {
        onSavedSearchTimeCountsUpdatedRef.current(data);
      }
    });

    // Dashboard stats updated events
    newSocket.on('dashboard:stats_updated', (data: { storeId: number }) => {
      onDashboardStatsUpdatedRef.current?.(data);
    });

    newSocket.on('order:bulk', (data) => {
      onOrderBulkRef.current?.(data);
    });

    newSocket.on('inbox:message', (data: { storeId: number }) => {
      onDashboardStatsUpdatedRef.current?.(data);
    });

    // Presence updates
    newSocket.on('presence:update', (data: PresenceUpdateEvent) => {
      if (onPresenceUpdatedRef.current) {
        onPresenceUpdatedRef.current(data);
      }
    });

    newSocket.on('order_discussion:typing', (data: OrderDiscussionTypingEvent) => {
      onDiscussionTypingRef.current?.(data);
    });

    setSocket(newSocket);

    // Page Visibility: emit focus/blur so server can set isActive on presence (avatars opacity)
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        newSocket.emit('presence:blur');
      } else {
        newSocket.emit('presence:focus');
      }
    };
    if (storeId) {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    // Cleanup on unmount
    return () => {
      if (storeId) {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
      if (storeId) {
        newSocket.emit('store:leave', storeId);
      }
      newSocket.close();
    };
  }, [storeId, accessToken]);

  return {
    socket,
    isConnected,
    error,
    emitOrderCreated,
    emitOrderUpdated,
    emitOrderDeleted,
    emitStatusChanged,
    emitCommentAdded,
    emitCommentDeleted
  };
};

export type { DeliveryUpdatedEvent };

